import { useLocation } from "@reach/router"
import * as d3 from "d3";
import * as React from 'react';

import Fluid from 'components/Fluid';
import { ReactComponent as InteractiveMap } from 'images/interactive_map.svg';
import NeighborhoodsProvider, { useNeighborhoodsData } from 'providers/NeighborhoodsProvider';

import NeighborhoodCard from './card';
import Pin from './pin';
import MapSlider from './slider';

const VIEWBOX = {
	width: 568.76,
	height: 329.95,
};

const DEFAULT_FOCUS_CENTER_OFFSET = { x: 0, y: -75 };
const FOCUS_PIN_COUNT = 3;
const FOCUS_PADDING = 128;
const MIN_FOCUS_SCALE = 1.75;
const MAX_FOCUS_SCALE = 25;
const PIN_SLIDER_ANIMATION_MS = 250;
const PIN_FADE_IN_DELAY_BASE_MS = 150;
const PIN_FADE_IN_STAGGER_MS = 70;

const normalizeKeyValue = (value) => String(value || '').trim().toLowerCase();

const markerMatchesSlideKey = (markerValue, slideKeyValue) => {
	const marker = normalizeKeyValue(markerValue);
	const slideKey = normalizeKeyValue(slideKeyValue);

	if (!marker || !slideKey) return false;
	return slideKey.includes(marker);
};

const getMarkerCenter = (element) => {
	const centerX = Number.parseFloat(element.getAttribute('cx'));
	const centerY = Number.parseFloat(element.getAttribute('cy'));

	if (Number.isFinite(centerX) && Number.isFinite(centerY)) {
		return { x: centerX, y: centerY };
	}

	try {
		const bounds = element.getBBox();
		if (!Number.isFinite(bounds.x) || !Number.isFinite(bounds.y)) return null;
		return {
			x: bounds.x + bounds.width / 2,
			y: bounds.y + bounds.height / 2,
		};
	} catch (error) {
		return null;
	}
};

const arePinsEqual = (prevPins, nextPins) => (
	prevPins.length === nextPins.length
	&& prevPins.every((pin, index) => (
		pin.permalink === nextPins[index].permalink
		&& Math.abs(pin.x - nextPins[index].x) < 0.25
		&& Math.abs(pin.y - nextPins[index].y) < 0.25
	))
);

const NeighborhoodsMapContent = ({
	title,
	useSlider,
	defaultIndex,
	flush,
	fullWidth,
	MapComponent = InteractiveMap,
	viewBox = VIEWBOX,
	className,
	svgKey = 'neighborhood',
	slideData,
	slideKey = 'permalink',
	focusPinCount = FOCUS_PIN_COUNT,
	focusPadding = FOCUS_PADDING,
	minFocusScale = MIN_FOCUS_SCALE,
	maxFocusScale = MAX_FOCUS_SCALE,
	trueCenterFocus = false,
	clampFocusX = false,
	initialFocusPermalinks,
	focusCenterOffset = DEFAULT_FOCUS_CENTER_OFFSET,
}) => {
	const { neighborhoods, currentNeighborhood } = useNeighborhoodsData();
	// Primitives (not the object ref) keep memoized callbacks stable if callers pass inline literals.
	const offsetX = focusCenterOffset?.x ?? DEFAULT_FOCUS_CENTER_OFFSET.x;
	const offsetY = focusCenterOffset?.y ?? DEFAULT_FOCUS_CENTER_OFFSET.y;

	const containerRef = React.useRef(null);
	const svgRef = React.useRef(null);
	const viewportRef = React.useRef(null);
	const currentTransformRef = React.useRef(d3.zoomIdentity);

	const [hasEnteredView, setHasEnteredView] = React.useState(false);
	const [hasAnimatedIn, setHasAnimatedIn] = React.useState(false);
	const [hasMeasuredPins, setHasMeasuredPins] = React.useState(false);
	const [isInteractive, setIsInteractive] = React.useState(false);
	const [hasSliderEntered, setHasSliderEntered] = React.useState(false);
	const [pins, setPins] = React.useState([]);
	const [activeNeighborhoodPermalink, setActiveNeighborhoodPermalink] = React.useState(null);
	const [focusNeighborhoodPermalinks, setFocusNeighborhoodPermalinks] = React.useState([]);
	const resizeRafRef = React.useRef(null);
	const sliderEntryTimeoutRef = React.useRef(null);
	const sliderRef = React.useRef(null);
	const skipNextSliderFocusRef = React.useRef(false);
	const hasInitializedDefaultRef = React.useRef(false);
	const markerAttributeName = React.useMemo(() => {
		const normalizedSvgKey = String(svgKey || 'neighborhood').trim().toLowerCase();
		return normalizedSvgKey ? `data-${normalizedSvgKey}` : 'data-neighborhood';
	}, [svgKey]);

	const slides = React.useMemo(() => {
		if (Array.isArray(slideData) && slideData.length > 0) return slideData;
		return neighborhoods;
	}, [neighborhoods, slideData]);

	const getSlideKeyValue = React.useCallback((slide) => {
		const rawValue = slide?.[slideKey];
		return rawValue === undefined || rawValue === null ? null : String(rawValue);
	}, [slideKey]);

	const getSlideCardData = React.useCallback((slide) => ({
		title: slide?.name || slide?.title || '',
		body: slide?.featuredDescription || slide?.description || slide?.body || slide?.distance || '',
		image: slide?.headerImage || slide?.image || '',
		href: slide?.uri || slide?.href || null,
		useLargeBody: slide?.useLargeBody || false,
	}), []);

	const neighborhoodIndexByPermalink = React.useMemo(() => {
		const indexMap = new Map();
		slides.forEach((slide, index) => {
			const slideValue = getSlideKeyValue(slide);
			if (!slideValue) return;
			indexMap.set(slideValue, index);
		});
		return indexMap;
	}, [getSlideKeyValue, slides]);

	const defaultNeighborhoodIndex = React.useMemo(() => {
		if (!slides.length) return null;

		if (typeof defaultIndex === 'number' && Number.isFinite(defaultIndex)) {
			const boundedIndex = Math.max(0, Math.min(slides.length - 1, Math.floor(defaultIndex)));
			return boundedIndex;
		}

		const isNeighborhoodDataSource = !Array.isArray(slideData) || slideData.length === 0;
		if (!isNeighborhoodDataSource || !currentNeighborhood) return null;

		const currentNeighborhoodPermalink = currentNeighborhood?.permalink;
		if (!currentNeighborhoodPermalink) return null;

		const normalizedCurrentNeighborhood = normalizeKeyValue(currentNeighborhoodPermalink);
		const index = slides.findIndex((slide) => normalizeKeyValue(getSlideKeyValue(slide)) === normalizedCurrentNeighborhood);
		return index >= 0 ? index : null;
	}, [currentNeighborhood, defaultIndex, getSlideKeyValue, slideData, slides]);

	const defaultNeighborhoodPermalink = getSlideKeyValue(slides[defaultNeighborhoodIndex]) || null;

	const applyTransform = React.useCallback((transform, animate = false, onEnd) => {
		if (!viewportRef.current) return;

		currentTransformRef.current = transform;
		const selection = d3.select(viewportRef.current);

		if (!animate) {
			selection.attr('transform', transform.toString());
			if (onEnd) onEnd();
			return;
		}

		selection
			.interrupt()
			.transition()
			.duration(3000)
			.ease(d3.easeExpIn)
			.attr('transform', transform.toString())
			.on('end', () => {
				if (onEnd) onEnd();
			});
	}, []);

	const handlePinClick = (event, { permalink, isActive }) => {
		if (!permalink) return;
		setActiveNeighborhoodPermalink(permalink);
		setFocusNeighborhoodPermalinks([permalink]);

		const pinNeighborhoodIndex = neighborhoodIndexByPermalink.get(permalink);
		if (sliderRef.current && typeof pinNeighborhoodIndex === 'number') {
			skipNextSliderFocusRef.current = true;
			sliderRef.current.slideTo(pinNeighborhoodIndex, PIN_SLIDER_ANIMATION_MS);
		}
	};

	const handleSlideHover = (permalink) => {
		if (!permalink) return;
		setActiveNeighborhoodPermalink(permalink);
		setFocusNeighborhoodPermalinks([permalink]);
	};

	const handleVisibleSlidesChange = React.useCallback(({ activeIndex, visiblePermalinks }) => {
		if (skipNextSliderFocusRef.current) {
			skipNextSliderFocusRef.current = false;
			return;
		}

		if (visiblePermalinks.length > 0) {
			setFocusNeighborhoodPermalinks(visiblePermalinks);
		}
	}, []);

	const getFocusPins = React.useCallback((pinCollection) => {
		if (trueCenterFocus && pinCollection.length <= focusPinCount) return pinCollection;
		if (focusNeighborhoodPermalinks.length === 0) return pinCollection;

		const focusPermalinkSet = new Set(focusNeighborhoodPermalinks);
		const focusedPins = pinCollection.filter((pin) => focusPermalinkSet.has(pin.permalink));

		return focusedPins.length > 0 ? focusedPins : pinCollection;
	}, [focusNeighborhoodPermalinks, focusPinCount, trueCenterFocus]);

	const getFocusTransformFromPins = React.useCallback((pinPoints) => {
		const svgElement = svgRef.current;

		if (!svgElement || pinPoints.length === 0) return null;

		const svgRect = svgElement.getBoundingClientRect();
		if (svgRect.width <= 0 || svgRect.height <= 0) return null;

		const scaleX = svgRect.width / viewBox.width;
		const scaleY = svgRect.height / viewBox.height;
		if (scaleX <= 0 || scaleY <= 0) return null;

		const selectedPins = [...pinPoints]
			.sort((a, b) => a.x - b.x)
			.slice(0, Math.min(focusPinCount, pinPoints.length));

		if (selectedPins.length === 0) return null;

		const userPoints = selectedPins.map((pin) => ({ x: pin.svgX, y: pin.svgY }));
		const centerPin = trueCenterFocus && pinPoints.length <= focusPinCount
			? selectedPins[Math.floor(selectedPins.length / 2)]
			: null;

		const minX = Math.min(...userPoints.map((point) => point.x));
		const maxX = Math.max(...userPoints.map((point) => point.x));
		const minY = Math.min(...userPoints.map((point) => point.y));
		const maxY = Math.max(...userPoints.map((point) => point.y));

		const centerX = centerPin ? centerPin.svgX : (minX + maxX) / 2;
		const centerY = centerPin ? centerPin.svgY : (minY + maxY) / 2;
		const widthSpan = Math.max(
			centerPin ? 2 * Math.max(...userPoints.map((point) => Math.abs(point.x - centerX))) : maxX - minX,
			1e-6,
		);
		const heightSpan = Math.max(
			centerPin ? 2 * Math.max(...userPoints.map((point) => Math.abs(point.y - centerY))) : maxY - minY,
			1e-6,
		);

		const availableWidth = Math.max(svgRect.width - focusPadding * 2, 1);
		const availableHeight = Math.max(svgRect.height - focusPadding * 2, 1);

		const fitScaleX = availableWidth / (widthSpan * scaleX);
		const fitScaleY = availableHeight / (heightSpan * scaleY);
		const targetScale = Math.max(minFocusScale, Math.min(maxFocusScale, Math.min(fitScaleX, fitScaleY)));

		let targetTranslateX = svgRect.width / (2 * scaleX) - centerX * targetScale + offsetX;
		if (clampFocusX && targetScale >= 1) {
			// Keep the map edges at or beyond the viewport edges so no empty gutter shows.
			const visibleWidth = svgRect.width / scaleX;
			targetTranslateX = Math.min(0, Math.max(visibleWidth - viewBox.width * targetScale, targetTranslateX));
		}
		const targetTranslateY = svgRect.height / (2 * scaleY) - centerY * targetScale + offsetY;

		return d3.zoomIdentity.translate(targetTranslateX, targetTranslateY).scale(targetScale);
	}, [clampFocusX, focusPadding, focusPinCount, maxFocusScale, minFocusScale, offsetX, offsetY, trueCenterFocus, viewBox]);

	const getInitialTransform = React.useCallback(() => {
		const svgElement = svgRef.current;
		if (!svgElement) return d3.zoomIdentity;

		const svgRect = svgElement.getBoundingClientRect();
		if (svgRect.width <= 0 || svgRect.height <= 0) return d3.zoomIdentity;

		const scaleX = svgRect.width / viewBox.width;
		const scaleY = svgRect.height / viewBox.height;
		if (scaleX <= 0 || scaleY <= 0) return d3.zoomIdentity;

		// Anchor the pre-focus view on the same offset center used for pin focusing.
		const centerX = viewBox.width / 2;
		const centerY = viewBox.height / 2;

		const translateX = svgRect.width / (2 * scaleX) - centerX;
		const translateY = svgRect.height / (2 * scaleY) - centerY;

		return d3.zoomIdentity.translate(translateX, translateY).scale(1);
	}, [viewBox]);

	const setMeasuredPins = React.useCallback((nextPins) => {
		setPins((prevPins) => (arePinsEqual(prevPins, nextPins) ? prevPins : nextPins));
	}, []);

	React.useEffect(() => {
		if (!containerRef.current) return;

		const svgElement = containerRef.current.querySelector('svg');
		if (!svgElement) return;

		svgRef.current = svgElement;

		const svg = d3.select(svgElement)
			.attr('viewBox', `0 0 ${viewBox.width} ${viewBox.height}`)
			.attr('preserveAspectRatio', 'xMidYMid meet')
			.attr('role', 'img')
			.attr('aria-label', 'Interactive South Walton Map');

		let viewport = svg.select('g[data-map-viewport="true"]');

		if (viewport.empty()) {
			viewport = svg.append('g').attr('data-map-viewport', 'true');
			const viewportNode = viewport.node();
			const defsNode = svg.select('defs').node();
			const children = Array.from(svgElement.children).filter((child) => child !== defsNode && child !== viewportNode);

			children.forEach((child) => {
				viewportNode.appendChild(child);
			});
		}

		viewportRef.current = viewport.node();
		applyTransform(getInitialTransform());
	}, [applyTransform, getInitialTransform, viewBox]);

	React.useEffect(() => {
		const container = containerRef.current;
		if (!container) return undefined;

		const observer = new IntersectionObserver(([entry]) => {
			if (!entry.isIntersecting) return;
			setHasEnteredView(true);
		}, {
			threshold: 0.25,
		});

		observer.observe(container);

		const rect = container.getBoundingClientRect();
		const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
		const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));
		const visibilityRatio = rect.height > 0 ? visibleHeight / rect.height : 0;

		if (visibilityRatio >= 0.25) {
			setHasEnteredView(true);
		}

		return () => {
			observer.disconnect();
		};
	}, []);

	const measurePins = React.useCallback(() => {
		const svgElement = svgRef.current;
		if (!svgElement) return [];

		const neighborhoodElements = d3
			.select(svgElement)
			.selectAll(`[${markerAttributeName}]`)
			.nodes();

		const svgRect = svgElement.getBoundingClientRect();
		if (svgRect.width <= 0 || svgRect.height <= 0) return [];

		const tmp = [];

		neighborhoodElements.forEach((element) => {
			const markerValue = element.getAttribute(markerAttributeName);
			if (!markerValue) return;
			const markerCenter = getMarkerCenter(element);
			const screenMatrix = element.getScreenCTM();
			if (!markerCenter || !screenMatrix) return;

			const slideDataForMarker = slides.find((slide) => markerMatchesSlideKey(markerValue, getSlideKeyValue(slide)));
			if (!slideDataForMarker) return;

			const cardData = getSlideCardData(slideDataForMarker);
			const slideValue = getSlideKeyValue(slideDataForMarker);
			if (!slideValue) return;

			const screenPoint = new DOMPoint(markerCenter.x, markerCenter.y).matrixTransform(screenMatrix);
			const finalX = screenPoint.x - svgRect.left;
			const finalY = screenPoint.y - svgRect.top;

			tmp.push({
				name: cardData.title || slideValue,
				permalink: slideValue,
				description: cardData.body,
				headerImage: cardData.image,
				uri: cardData.href,
				svgX: markerCenter.x,
				svgY: markerCenter.y,
				x: finalX,
				y: finalY,
			});
		});

		return tmp.sort((a, b) => a.x - b.x);
	}, [getSlideCardData, getSlideKeyValue, markerAttributeName, slides]);

	React.useEffect(() => {
		if (!hasEnteredView) return;
		setMeasuredPins(measurePins());
		setHasMeasuredPins(true);
	}, [hasEnteredView, measurePins, setMeasuredPins]);

	React.useEffect(() => {
		if (!hasEnteredView) return;
		setMeasuredPins(measurePins());
		setHasMeasuredPins(true);
	}, [hasEnteredView, markerAttributeName, measurePins, setMeasuredPins]);

	React.useEffect(() => {
		if (!slides.length || hasInitializedDefaultRef.current) return;

		if (defaultNeighborhoodPermalink) {
			setActiveNeighborhoodPermalink(defaultNeighborhoodPermalink);
			setFocusNeighborhoodPermalinks(
				Array.isArray(initialFocusPermalinks) && initialFocusPermalinks.length > 0
					? initialFocusPermalinks
					: [defaultNeighborhoodPermalink],
			);
		}

		hasInitializedDefaultRef.current = true;
	}, [defaultNeighborhoodPermalink, initialFocusPermalinks, slides]);

	React.useEffect(() => {
		if (!useSlider || !sliderRef.current || !defaultNeighborhoodPermalink) return;

		const defaultIndex = neighborhoodIndexByPermalink.get(defaultNeighborhoodPermalink);
		if (typeof defaultIndex === 'number') {
			sliderRef.current.slideTo(defaultIndex, 0, false);
			setActiveNeighborhoodPermalink(defaultNeighborhoodPermalink);
		}
	}, [defaultNeighborhoodPermalink, neighborhoodIndexByPermalink, useSlider]);

	React.useEffect(() => {
		if (!hasEnteredView || hasAnimatedIn || pins.length === 0 || !viewportRef.current) return;

		const dynamicFocusTransform = getFocusTransformFromPins(getFocusPins(pins));
		if (!dynamicFocusTransform) return;

		applyTransform(dynamicFocusTransform, true, () => {
			setMeasuredPins(measurePins());
			setHasAnimatedIn(true);
			setIsInteractive(true);
		});
	}, [applyTransform, getFocusPins, getFocusTransformFromPins, hasAnimatedIn, hasEnteredView, measurePins, pins, setMeasuredPins]);

	React.useEffect(() => {
		if (!hasEnteredView || hasAnimatedIn || !hasMeasuredPins) return;
		if (pins.length > 0) return;

		setHasAnimatedIn(true);
		setIsInteractive(true);
	}, [hasAnimatedIn, hasEnteredView, hasMeasuredPins, pins.length]);

	React.useEffect(() => {
		if (!hasEnteredView || !hasAnimatedIn) return undefined;
		const container = containerRef.current;
		if (!container) return undefined;

		const observer = new ResizeObserver(() => {
			if (resizeRafRef.current) {
				window.cancelAnimationFrame(resizeRafRef.current);
			}

			resizeRafRef.current = window.requestAnimationFrame(() => {
				const measuredPins = measurePins();
				const dynamicFocusTransform = getFocusTransformFromPins(getFocusPins(measuredPins));

				if (dynamicFocusTransform) {
						applyTransform(dynamicFocusTransform, false, () => {
							setMeasuredPins(measurePins());
						});
				} else {
					setMeasuredPins(measuredPins);
				}

				resizeRafRef.current = null;
			});
		});

		observer.observe(container);

		return () => {
			observer.disconnect();
			if (resizeRafRef.current) {
				window.cancelAnimationFrame(resizeRafRef.current);
				resizeRafRef.current = null;
			}
		};
	}, [applyTransform, getFocusPins, getFocusTransformFromPins, hasAnimatedIn, hasEnteredView, measurePins, setMeasuredPins]);

	React.useEffect(() => {
		if (!hasAnimatedIn || !hasEnteredView || pins.length === 0) return;

		const dynamicFocusTransform = getFocusTransformFromPins(getFocusPins(pins));
		if (!dynamicFocusTransform) return;

		applyTransform(dynamicFocusTransform, false, () => {
			setMeasuredPins(measurePins());
		});
	}, [applyTransform, getFocusPins, getFocusTransformFromPins, hasAnimatedIn, hasEnteredView, measurePins, pins, setMeasuredPins]);

	React.useEffect(() => {
		if (!useSlider) {
			setHasSliderEntered(false);
			return undefined;
		}

		if (!isInteractive) {
			setHasSliderEntered(false);
			return undefined;
		}

		const maxPinDelay = Math.max(0, pins.length - 1) * PIN_FADE_IN_STAGGER_MS;
		const sliderDelayMs = PIN_FADE_IN_DELAY_BASE_MS + maxPinDelay;

		sliderEntryTimeoutRef.current = window.setTimeout(() => {
			setHasSliderEntered(true);
			sliderEntryTimeoutRef.current = null;
		}, sliderDelayMs);

		return () => {
			if (sliderEntryTimeoutRef.current) {
				window.clearTimeout(sliderEntryTimeoutRef.current);
				sliderEntryTimeoutRef.current = null;
			}
		};
	}, [isInteractive, pins.length, useSlider]);

	return (
		<Fluid
			layout="12"
			bleed={fullWidth ? "both" : undefined}
			className={[
				'neighborhoodsMap',
				className,
				isInteractive ? 'neighborhoodsMap--interactive' : undefined,
				(flush ? 'margin-flush' : ''),
			].join(' ')}
		>
			<div
				aria-label="South Walton Neighborhoods Map"
				className={`neighborhoodsMap__interactive${isInteractive ? ' neighborhoodsMap__interactive--interactive' : ''}`}
				ref={containerRef}
			>
				<MapComponent />
				<div className={`neighborhoodsMap__pins${isInteractive ? ' neighborhoodsMap__pins--interactive' : ''}`}>
					{pins.map((pin, index) => (
						<Pin
							key={pin.name}
							variant="map_icon"
							isActive={activeNeighborhoodPermalink === pin.permalink ? true : undefined}
							onClick={(e) => handlePinClick(e, {
								permalink: pin.permalink,
								isActive: activeNeighborhoodPermalink === pin.permalink,
							})}
							style={{
								position: 'absolute',
								left: pin.x,
								top: pin.y,
								transform: 'translate(-50%, -100%)',
								animationDelay: `${0.15 + index * 0.07}s`,
							}}
							aria-label={`Map pin for ${pin.name} neighborhood`}
						/>
					))}
				</div>
			</div>
			{useSlider && <MapSlider
				title={title}
				hasEnteredView={hasSliderEntered}
				initialSlide={defaultNeighborhoodIndex}
				permalinks={slides.map((slide) => getSlideKeyValue(slide))}
				onSwiperReady={(swiperInstance) => {
					sliderRef.current = swiperInstance;
				}}
				// onSlideClick={handlePinClick}
				onVisibleSlidesChange={handleVisibleSlidesChange}
			>
				{slides.map((slide, index) => {
					const slideValue = getSlideKeyValue(slide) || `slide-${index}`;
					const cardData = getSlideCardData(slide);
					return (
					<div 
						key={slideValue}
						onMouseEnter={() => {
							setActiveNeighborhoodPermalink(slideValue);
						}}
						onMouseLeave={() => {
							setActiveNeighborhoodPermalink(null);
						}}
					>
						<NeighborhoodCard
							title={cardData.title}
							body={cardData.body}
							useLargeBody={cardData.useLargeBody}
							image={cardData.image}
							href={cardData.href}
							isActive={activeNeighborhoodPermalink === slideValue}
							onMouseEnter={() => handleSlideHover(slideValue)}
						/>
					</div>
					);
				})}
			</MapSlider>}
		</Fluid>
	);
}

const NeighborhoodsMap = ({ title, useSlider = true, defaultIndex = null, ...props }) => {
	const location = useLocation();

	return (
		<NeighborhoodsProvider location={location}>
			<NeighborhoodsMapContent title={title} useSlider={useSlider} defaultIndex={defaultIndex} {...props} />
		</NeighborhoodsProvider>
	);
}

export { NeighborhoodsMapContent };
export default React.memo(NeighborhoodsMap);